/**
 * @warning
 * THIS FILE CONTAINS SHARED LOGIC / EXPECTATIONS WITH AI ENGINE.
 * For logic changes, ensure AI/Amir is looped in to any changes that may impact the AI engine.
 */

/**
 * This file deals with converting Timely `Entry`s to TIC `Memory`s
 *
 * @note
 * TIC's TimelyProvider needs better understanding of Timely's Memories, which is why
 * these transformers are located in the SDK instead of Timely directly.
 *
 * I also expect this will pay dividends if/when "migrate to TIC" becomes a thing.
 */

import {
  type EmbeddedToolLight,
  type Memory,
  MemorySourceEnumSchema,
} from '@timelyapp/integration-cloud-api-spec';
import find from 'lodash-es/find';
import { DateTime } from 'luxon';

/**
 * @todo Map all existing Timely integrations/apps to TIC `Tool`s
 * @link https://linear.app/timelyapp/issue/TT-883/[timely-frontend]-convert-legacy-integration-based-memories-to-tic
 * @link https://github.com/Timely/timely/blob/master/config/apps.yml
 */

const entryApplicationOrTypeToToolMap: Record<string, Partial<EmbeddedToolLight> | undefined> = {
  github: {
    id: 'github',
    title: 'GitHub',
    raw: 'GitHub',
  },
  gps_tracker: {
    id: 'location_tracker',
    title: 'Location Tracking',
    raw: 'Location Tracking',
  },
  google_calendar: {
    id: 'google_calendar',
    title: 'Google Calendar',
    raw: 'Google Calendar',
  },
  gmail: {
    id: 'gmail',
    title: 'Gmail',
    raw: 'Gmail',
  },
  trello: {
    id: 'trello',
    title: 'Trello',
    raw: 'Trello',
  },
  asana: {
    id: 'asana',
    title: 'Asana',
    raw: 'Asana',
  },
  /**
   * Outlook has two identifiers on Timely, `outlook` and `outlook_mail`.
   * @link https://github.com/Timely/timely/blob/07984806b4ab5757d8fe6e917ab70c64470c6cd0/app/models/app.rb#L8-L10
   */
  outlook_mail: {
    id: 'microsoft_outlook',
    title: 'Microsoft Outlook',
    raw: 'Microsoft Outlook',
  },
  outlook: {
    id: 'microsoft_outlook',
    title: 'Microsoft Outlook',
    raw: 'Microsoft Outlook',
  },
  todoist: {
    id: 'todoist',
    title: 'Todoist',
    raw: 'Todoist',
  },
  jira: {
    id: 'jira',
    title: 'Jira',
    raw: 'Jira',
  },
  zoom: {
    id: 'zoom',
    title: 'Zoom',
    raw: 'Zoom',
  },
  basecamp4: {
    id: 'basecamp',
    title: 'Basecamp',
    raw: 'Basecamp',
  },
  basecamp3: {
    id: 'basecamp',
    title: 'Basecamp',
    raw: 'Basecamp',
  },
  teams: {
    id: 'microsoft_teams',
    title: 'Microsoft Teams',
    raw: 'Microsoft Teams',
  },
  office365: {
    id: 'microsoft_outlook_calendar',
    title: 'O365 Calendar',
    raw: 'Office 365 Calendar',
  },
};

/**
 * Attempts to match a Timely `Entry` to a TIC `Tool`.
 */
const entryApplicationToTool = (entry: Entry): EmbeddedToolLight => {
  const application = find(entry?.extra_attributes, { name: 'application' })?.value || entry.type;
  let tool;

  // First, match against defined tools
  if (application) {
    tool = entryApplicationOrTypeToToolMap[application];
  }
  // Or, if there's a url, use that as the tool
  if (!tool && entry.url) {
    tool = { id: null, title: entry.url, raw: entry.url };
  }
  // Last resort, use the `application` as the tool
  if (!tool && application) {
    tool = { id: null, title: application, raw: application };
  }

  if (!tool) console.log('No tool found for entry', entry);

  return {
    // "Unknown tools" from legacy don't have real tool.id's, so we're handling this edge-case of ''
    // when creating tool configs. Probably want to re-visit this with a "legacy tool" type.
    id: tool?.id || '',
    title: tool?.title || application || 'Unknown tool',
    raw: tool?.raw || application,
    icon_url: tool?.icon_url || getIconUrl(entry) || null,
  };
};

// Construct icon CDN URL from entry
// Mostly duplicated from https://github.com/Timely/Frontend/blob/master/apps/timely-frontend/src/factories/timeline.ts#L554-L578
const getIconUrl = (entry: Entry) => {
  let icon = entry.icon_url;

  // App icon via CDN?
  const appAttribute = entry.extra_attributes?.find(a => a.name === 'application');
  if (appAttribute) {
    const application = encodeURIComponent(appAttribute.value);
    icon = `https://icons.timelyapp.com/apps/${getIconPlatform(
      entry.type
    )}/${application}?size=64&fallback=${encodeURIComponent(icon)}`;
  }

  // Favicon via CDN?
  const url = find(entry?.extra_attributes, { name: 'detail' })?.value;
  if (url?.startsWith('http')) {
    try {
      const hostname = new URL(url).hostname;
      if (hostname) {
        icon = `https://icons.timelyapp.com/favicons/${hostname}?size=64&fallback=${entry.icon_url}`;
      }
    } catch (e) {}
  }
  return icon;
};

// Borrowed from https://github.com/Timely/Frontend/blob/master/apps/timely-frontend/src/factories/timeline.ts#L628
function getIconPlatform(type?: string) {
  const lowercasedType = type?.toLowerCase();
  if (lowercasedType === 'macos') return 'mac';
  if (lowercasedType === 'windows') return 'windows';
  return 'linux';
}

type SafeDurationBase = {
  from: string;
  to?: string;
};
/**
 * Integration-based Memories may not have a duration, so minimums are added.
 * By default, memories < 2mins are hidden (per minimumGroupDurationMinutes filter),
 * so we must ensure memories are at least 2mins long.
 */

function getSafeDuration(entry: SafeDurationBase, minimumDurationMinutes = 2) {
  const started_at = DateTime.fromISO(entry.from);
  let ended_at = entry.to
    ? DateTime.fromISO(entry.to)
    : started_at.plus({ minutes: minimumDurationMinutes });

  if (ended_at.diff(started_at, 'minutes').minutes < minimumDurationMinutes) {
    ended_at = started_at.plus({ minutes: minimumDurationMinutes });
  }

  return {
    started_at: started_at.toISO()!,
    ended_at: ended_at.toISO()!,
    total_seconds: ended_at.diff(started_at, 'seconds').seconds,
  };
}

/**
 * Converts a Timely `Entry` to a TIC `Memory`
 */
export const timelyEntryToTicMemory = (entries: Entry[]): Memory[] => {
  return entries.reduce((acc, entry) => {
    // Find application
    const tool = entryApplicationToTool(entry);
    const window_tool = null;

    // Find url
    const url = find(entry?.extra_attributes, { name: 'detail' })?.value;

    // Shared with subMemory
    const color = entry.color || 'rgba(86,210,255,0.30)';
    const icon_url = tool.icon_url || entry.icon_url || entry.icon_fallback_url || null;
    const ignored = entry.spam;

    const { started_at, ended_at, total_seconds } = getSafeDuration(entry);

    // Ensure tracker_source is consistent between TIC & Timely memories
    // Alternatively, parse via MemorySourceEnumSchema.safeParse but likely less performant?
    let entryType = entry.type?.toLowerCase() as Memory['tracker_source'];
    if ((entryType as string) === 'macos') {
      entryType = 'mac';
    }
    const tracker_source =
      entryType && Object.values(MemorySourceEnumSchema.enum).includes(entryType)
        ? entryType
        : null;

    const baseMemory = {
      id: entry.id,
      title: entry.title,
      description: entry.description || entry.note,
      icon_url,
      started_at,
      ended_at,
      updated_at: new Date(entry.to).toISOString(),
      total_seconds,
      color,
      tool,
      window_tool,
      url,
      ignored,
      from_timely: true,
      tracker_source,
    } satisfies Memory;

    acc.push(baseMemory);

    if (Array.isArray(entry.sub_entries)) {
      const subEntries = entry.sub_entries
        // Some sub_entries are duplicates of the parent entry, so we filter them out.
        .filter(sub_entry => {
          const isDuplicate = sub_entry.note === entry.note && sub_entry.from === entry.from;
          return !isDuplicate;
        })
        .map(sub_entry => {
          const { started_at, ended_at, total_seconds } = getSafeDuration(sub_entry);
          return {
            id: sub_entry.id,
            title: sub_entry.note || 'No note',
            icon_url,
            started_at,
            ended_at,
            updated_at: new Date(sub_entry.to).toISOString(),
            total_seconds,
            color,
            tool,
            window_tool,
            ignored,
            from_timely: true,
            tracker_source,
          } satisfies Memory;
        });

      acc.push(...subEntries);
    }

    return acc;
  }, [] as Memory[]);
};

/**
 * Types DUPLICATED from `timely-frontend/src/types/timeline.ts`
 */
type EntryAttributes = Array<{
  name: string;
  value: string;
}>;

interface MemorySubEntities {
  id: number;
  uid: string;
  entry_id: number;
  // ^^ wtf 3 ids?
  from: string;
  to: string;
  note: string;
}

interface Duration {
  hours: number;
  minutes: number;
  seconds: number;
  formatted: string;
  total_hours: number;
  total_seconds: number;
  total_minutes: number;
}

interface Entry {
  id: number;
  uid: string;
  at: string | null | undefined;
  color: string;
  date: string;
  type: string;
  spam: boolean;
  importance: number;
  duration: Duration;
  entity_type: string | null | undefined;
  extra_attributes: EntryAttributes;
  from: string;
  to: string;
  icon: string;
  icon_url: string;
  icon_fallback_url: string;
  description: string;
  sub_entries: Array<MemorySubEntities>;
  title: string;
  note: string | null;
  url: string | null;
  updated_by: 'User' | 'AI';
}
